import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyleKit } from '@tiptap/extension-text-style' // This is correct
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';

import { SlashCommand, setInsertReferenceCallback } from './SlashCommand';
import { ReferenceBlock } from './ReferenceBlock';
import { FaqExtensions, FAQ_BLOCK_CONTENT } from './FaqBlock';
import { Callout } from './Callout';
import { TableMenu } from './TableMenu';
import { ReferenceBlockSelector } from './ReferenceBlockSelector';
import { AssetManager } from './AssetManager';
import { ImageBubbleMenu } from './ImageBubbleMenu';
import { CustomImage } from './CustomImage';
import type { HelpCenterStructureNode } from '@/types';
import { 
  Bold, 
  Italic, 
  Strikethrough, 
  Code, 
  Heading1, 
  Heading2, 
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Link as LinkIcon,
  Image as ImageIcon,
  Code2,
  FileText,
  HelpCircle,
  Maximize2,
  Minimize2,
  Table as TableIcon,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface TipTapEditorProps {
  content: any; // TipTap JSON content
  onChange: (json: any, html: string, text: string) => void;
  placeholder?: string;
  editable?: boolean;
  helpCenterStructure?: HelpCenterStructureNode[]; // Help center structure for reference blocks
  helpCenterSlug?: string; // Help center slug for URL construction
}


export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  content,
  onChange,
  placeholder,
  editable = true,
  helpCenterStructure,
  helpCenterSlug,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('editor.tipTap.startWriting');
  const [showReferenceSelector, setShowReferenceSelector] = React.useState(false);
  const [showAssetManager, setShowAssetManager] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  // Set the callback for slash command
  React.useEffect(() => {
    setInsertReferenceCallback(() => setShowReferenceSelector(true));
  }, []);

  // Exit writing (fullscreen) mode on Escape
  React.useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        codeBlock: {
          HTMLAttributes: {
            class: 'bg-muted p-4 rounded-md font-mono text-sm',
          },
        },
      }),
      Placeholder.configure({
        placeholder: resolvedPlaceholder,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline cursor-pointer',
        },
      }),
      CustomImage.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
      Color,
      TextStyleKit,
      SlashCommand,
      ReferenceBlock,
      ...FaqExtensions,
      Callout,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      const html = editor.getHTML();
      const text = editor.getText();
      onChange(json, html, text);
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor focus:outline-none min-h-[400px] px-4 py-3',
      },
    },
  });

  // Update editor content when content prop changes (e.g., when importing a document)
  // This allows external content updates to be reflected in the editor
  React.useEffect(() => {
    if (!editor) return;
    
    const currentContent = JSON.stringify(editor.getJSON());
    const newContent = JSON.stringify(content);
    
    // Only update if content actually changed to avoid unnecessary updates
    // This is important for document imports and other programmatic content updates
    if (currentContent !== newContent) {
      // setContent will update the editor and trigger onUpdate, which calls onChange
      // This ensures the editor view is updated and parent state stays in sync
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const setLink = useCallback(() => {
    if (!editor) return;

    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt(t('editor.tipTap.urlPromptLabel'), previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor, t]);

  const addImage = useCallback(() => {
    if (!editor) return;
    setShowAssetManager(true);
  }, [editor]);

  const handleImageSelect = useCallback((url: string) => {
    if (!editor) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const handleInsertReference = useCallback((item: { id: string; type: 'guide' | 'article'; title: string; subtitle?: string; url?: string; helpCenterSlug?: string }) => {
    if (!editor) return;

    editor
      .chain()
      .focus()
      .insertContent({
        type: 'referenceBlock',
        attrs: {
          id: item.id,
          type: item.type,
          title: item.title,
          subtitle: item.subtitle,
          url: item.url,
          helpCenterSlug: item.helpCenterSlug,
        },
      })
      .run();
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex flex-col bg-background',
        isFullscreen
          ? 'fixed inset-0 z-50'
          : 'border rounded-lg overflow-hidden'
      )}
      style={isFullscreen ? undefined : { maxHeight: 'calc(100vh - 200px)' }}
    >
      {/* Toolbar - Sticky */}
      <div className="flex items-center gap-1 p-2 bg-muted border-b flex-wrap sticky top-0 z-10 backdrop-blur-sm bg-muted/95">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          data-active={editor.isActive('bold')}
          title={t('editor.tipTap.bold')}
        >
          <Bold className={cn("h-4 w-4", editor.isActive('bold') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          title={t('editor.tipTap.italic')}
        >
          <Italic className={cn("h-4 w-4", editor.isActive('italic') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editor.can().chain().focus().toggleStrike().run()}
          title={t('editor.tipTap.strikethrough')}
        >
          <Strikethrough className={cn("h-4 w-4", editor.isActive('strike') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().toggleCode().run()}
          disabled={!editor.can().chain().focus().toggleCode().run()}
          title={t('editor.tipTap.code')}
        >
          <Code className={cn("h-4 w-4", editor.isActive('code') && "text-primary")} />
        </Button>
        
        <Separator orientation="vertical" className="h-4" />
        
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => {
            if (editor.isActive('heading', { level: 1 })) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level: 1 }).run();
            }
          }}
          title={t('editor.tipTap.heading1')}
        >
          <Heading1 className={cn("h-4 w-4", editor.isActive('heading', { level: 1 }) && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => {
            if (editor.isActive('heading', { level: 2 })) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level: 2 }).run();
            }
          }}
          title={t('editor.tipTap.heading2')}
        >
          <Heading2 className={cn("h-4 w-4", editor.isActive('heading', { level: 2 }) && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          onClick={() => {
            if (editor.isActive('heading', { level: 3 })) {
              editor.chain().focus().setParagraph().run();
            } else {
              editor.chain().focus().setHeading({ level: 3 }).run();
            }
          }}
          title={t('editor.tipTap.heading3')}
        >
          <Heading3 className={cn("h-4 w-4", editor.isActive('heading', { level: 3 }) && "text-primary")} />
        </Button>
        
        <Separator orientation="vertical" className="h-4" />
        
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => {
            if (editor.isActive('bulletList')) {
              editor.chain().focus().liftListItem('listItem').run();
            } else {
              editor.chain().focus().toggleBulletList().run();
            }
          }}
          title={t('editor.tipTap.bulletList')}
        >
          <List className={cn("h-4 w-4", editor.isActive('bulletList') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => {
            if (editor.isActive('orderedList')) {
              editor.chain().focus().liftListItem('listItem').run();
            } else {
              editor.chain().focus().toggleOrderedList().run();
            }
          }}
          title={t('editor.tipTap.numberedList')}
        >
          <ListOrdered className={cn("h-4 w-4", editor.isActive('orderedList') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => {
            editor.chain().focus().toggleBlockquote().run();
          }}
          title={t('editor.tipTap.quote')}
        >
          <Quote className={cn("h-4 w-4", editor.isActive('blockquote') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => {
            editor.chain().focus().toggleCodeBlock().run();
          }}
          title={t('editor.tipTap.codeBlock')}
        >
          <Code2 className={cn("h-4 w-4", editor.isActive('codeBlock') && "text-primary")} />
        </Button>
        
        <Separator orientation="vertical" className="h-4" />
        
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={setLink}
          data-active={editor.isActive('link')}
          title={t('editor.tipTap.link')}
        >
          <LinkIcon className={cn("h-4 w-4", editor.isActive('link') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={addImage}
          title={t('editor.tipTap.image')}
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setShowReferenceSelector(true)}
          title={t('editor.tipTap.insertReference')}
        >
          <FileText className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().insertContent(FAQ_BLOCK_CONTENT).run()}
          title={t('editor.tipTap.insertFaq')}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          disabled={!editor.can().chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title={t('editor.tipTap.insertTable')}
        >
          <TableIcon className={cn("h-4 w-4", editor.isActive('table') && "text-primary")} />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().toggleCallout({ type: 'info' }).run()}
          title={t('editor.tipTap.callout')}
        >
          <Info className={cn("h-4 w-4", editor.isActive('callout') && "text-primary")} />
        </Button>

        <Separator orientation="vertical" className="h-4" />
        
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          title={t('editor.tipTap.undo')}
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          title={t('editor.tipTap.redo')}
        >
          <Redo className="h-4 w-4" />
        </Button>

        <Separator orientation="vertical" className="h-4 ml-auto" />

        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => setIsFullscreen((v) => !v)}
          title={isFullscreen ? t('editor.tipTap.exitWritingMode') : t('editor.tipTap.writingMode')}
        >
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </Button>
      </div>

      {/* Editor Content - Scrollable */}
      <div className="overflow-y-auto flex-1">
        <div className={cn(isFullscreen && 'max-w-3xl mx-auto w-full')}>
          <EditorContent editor={editor} />
        </div>
      </div>

      {/* Reference Block Selector */}
      <ReferenceBlockSelector
        isOpen={showReferenceSelector}
        onClose={() => setShowReferenceSelector(false)}
        onSelect={handleInsertReference}
        helpCenterStructure={helpCenterStructure || []}
        helpCenterSlug={helpCenterSlug}
      />

      {/* Asset Manager */}
      <AssetManager
        isOpen={showAssetManager}
        onClose={() => setShowAssetManager(false)}
        onSelect={handleImageSelect}
      />

      {/* Image Bubble Menu */}
      {editor && <ImageBubbleMenu editor={editor} />}

      {/* Table controls (shown while the cursor is inside a table) */}
      {editor && <TableMenu editor={editor} />}
    </div>
  );
};
